/**
 * Booking service - handles all booking-related API operations
 */

import { BaseApiClient } from '../core/base-client';
import { 
  CreateBookingRequest, 
  BookingResponse, 
  FullBookingData, 
  BookingListResponse,
  BookingSearchFilters,
  TopUserSpacesResponse,
  BookingCalculationMethod,
  ApiResponse
} from '../types';

export class BookingService {
  constructor(private client: BaseApiClient) {}

  /**
   * Create a new booking
   */
  async createBooking(bookingData: CreateBookingRequest): Promise<BookingResponse> {
    // The API returns ApiResponse<CoworkingManagerBooking>
    const response = await this.client.post<{
      success: boolean;
      data: {
        // Fields from CoworkingManagerBooking model
        id: number;
        title: string;
        startDate: string;
        endDate: string;
        spaceName: string;
        propertyName: string;
        spaceUnitPrice: number;
        bookingStatus: string;
        message?: string;
      };
      message: string;
    }>('/api/bookings', bookingData);
    
    // Extract the booking data from the API response wrapper
    const booking = response.data;
    
    return {
      id: booking.id,
      title: booking.title,
      startDate: booking.startDate,
      endDate: booking.endDate,
      spaceName: booking.spaceName,
      totalAmount: booking.spaceUnitPrice || 0,
      status: booking.bookingStatus || 'Confirmed',
      message: booking.message || response.message
    };
  }

  /**
   * Get a single booking by ID.
   * Now includes available actions and calculated status from backend business logic.
   */
  async getBooking(bookingId: number): Promise<FullBookingData | null> {
    try {
      const response = await this.client.get<{
        success: boolean;
        data: FullBookingData;
        message: string;
      }>(`/api/bookings/${bookingId}`);
      
      if (response.success) {
        return response.data;
      }
      return null;
    } catch (error) {
      console.error('Error fetching booking:', error);
      return null;
    }
  }

  /**
   * Cancel a booking
   */
  async cancelBooking(bookingId: number): Promise<{ success: boolean; message: string }> {
    try {
      const response = await this.client.delete<{
        success: boolean;
        data: {
          success: number;
          message: string;
          errorMessage?: string;
        };
        message: string;
      }>(`/api/bookings/${bookingId}`);
      
      // Check both API wrapper success and external API success
      const isSuccess = response.success && response.data.success === 1;
      
      return {
        success: isSuccess,
        message: response.data.message || response.message || 'Booking cancelled successfully'
      };
    } catch (error: any) {
      console.error('Error cancelling booking:', error);
      
      // Handle API error responses
      if (error.response?.data?.data?.errorMessage) {
        return {
          success: false,
          message: error.response.data.data.errorMessage
        };
      }
      
      return {
        success: false,
        message: 'Failed to cancel booking. Please try again.'
      };
    }
  }

  /**
   * Get bookings with optional filters.
   * Now includes available actions and calculated status for each booking from backend business logic.
   */
  async getBookings(filters?: BookingSearchFilters): Promise<BookingListResponse> {
    try {
      // Convert filters to query parameters for GET request
      const queryParams = new URLSearchParams();
      
      if (filters) {
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            if (Array.isArray(value)) {
              // Handle array values
              value.forEach(v => queryParams.append(key, v.toString()));
            } else if (value instanceof Date) {
              // Handle Date objects
              queryParams.append(key, value.toISOString());
            } else {
              queryParams.append(key, value.toString());
            }
          }
        });
      }
      
      const queryString = queryParams.toString();
      const url = queryString ? `/api/bookings?${queryString}` : '/api/bookings';
      
      const response = await this.client.get<{
        success: boolean;
        data: {
          bookings: FullBookingData[];
          totalRows: number;
        };
        message: string;
      }>(url);
      
      // Return the expected structure for BookingListResponse
      if (response.success && response.data) {
        return {
          bookings: response.data.bookings || [],
          totalRows: response.data.totalRows || 0
        };
      }
      
      // Return empty result if API call failed
      return {
        bookings: [],
        totalRows: 0
      };
    } catch (error) {
      console.error('Error fetching bookings:', error);
      return {
        bookings: [],
        totalRows: 0
      };
    }
  }

  /**
   * Get user's top 3 most booked spaces for dashboard display.
   * Uses advanced analytics to determine personalized space recommendations based on booking history.
   */
  async getUserTopSpaces(
    calculationMethod: BookingCalculationMethod = BookingCalculationMethod.RecencyWeighted,
    timeframeDays: number = 365
  ): Promise<TopUserSpacesResponse> {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append('calculationMethod', calculationMethod);
      queryParams.append('timeframeDays', timeframeDays.toString());
      
      const response = await this.client.get<ApiResponse<TopUserSpacesResponse>>(
        `/api/bookings/user/top-spaces?${queryParams}`
      );
      
      if (response.success && response.data) {
        return response.data;
      }
      
      // Return empty response if no data or not successful
      return {
        topSpaces: [],
        hasBookingHistory: false,
        totalBookingsAnalyzed: 0,
        totalUniqueSpaces: 0,
        calculationMethod: calculationMethod,
        timeframeDays: timeframeDays
      };
      
    } catch (error) {
      console.warn('Failed to fetch user top spaces:', error);
      // Return empty response on error to prevent UI breaking
      return {
        topSpaces: [],
        hasBookingHistory: false,
        totalBookingsAnalyzed: 0,
        totalUniqueSpaces: 0,
        calculationMethod: calculationMethod,
        timeframeDays: timeframeDays
      };
    }
  }
}

/**
 * Factory function to create booking service
 */
export function createBookingService(client: BaseApiClient): BookingService {
  return new BookingService(client);
} 